<template>
  <v-container>
    <OnexSkeletonLoader v-if="isLoading" type="article" />
    <v-card v-else class="pb-4 card-wrapper mb-4">
      <Alerts context="childrider" />
      <v-card-title class="justify-center">
        <h3 role="heading" aria-level="2" class="text-h2 pt-1 text-center">
          {{ $t('L_other_acknowledgements') }}
        </h3>
      </v-card-title>
      <v-spacer class="pt-2" />
      <v-card-text class="text-content frame-parent mb-0 pb-0">
        <v-card class="overflow-y-auto">
          <v-card-text
            id="scroll-target"
            v-scroll:#scroll-target="onScroll"
            column
            align-center
            justify-center
            style="max-height: 400px"
          >
            <div v-for="(item, index) in acknowledgementHTML" :key="index">
              <div v-html="item" />
              <br v-if="Number(index) % 2 === 0" />
            </div>
          </v-card-text>
        </v-card>
        <v-row justify="center">
          <v-col cols="9" class="text-center mt-8 cursor-pointer" @click="openPDF">
            <v-icon small class="ml-2 mr-2 text-h1 color-primary-text">
              {{ mdiFilePdfBox }}
            </v-icon>
            <span class="text-h4 color-primary-text">
              {{ $t('L_child_rider_exchange_package') + ' ' + generationDate }}
            </span>

            <ChildRiderExchangePackage
              v-if="showModal"
              :show-modal="showModal"
              @close="showModal = false"
            />
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="12" sm="11" md="9" class="pb-0">
            <v-checkbox
              color="primary"
              v-model="accepted"
              :label="$t('L_by_checking_the_box_I_agree')"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-center text-center">
        <v-col cols="8" md="4" class="pt-0 pb-0">
          <OnexButton
            class="mb-16 i_agree_btn"
            :loading="loading"
            id="i_agree"
            color="primary"
            :disabled="!accepted"
            @click="submit"
          >
            {{ $t('L_i_agree') }}
          </OnexButton>
        </v-col>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { formatDate } from '@/shared/util/formatters';
import { openInNewWindow } from '@/mixins/openNewWindow';
import ChildRiderExchangePackage from './ChildRiderExchangePackage.vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapActions, mapState, mapStores } from 'pinia';
import AlertsComponent from '@/components/AlertsComponent.vue';
import { DOCUMENT_OBJECT_TYPES } from '@/shared/models/constants.models';
import { useChildRiderStore } from '../../store/childrider.store';
import { OnexButton, OnexSkeletonLoader } from '@primerica/onex-ui-lib';
import { mdiFilePdfBox } from '@mdi/js';
const componentOptions: ComponentOptions = {
  name: 'CRCStepE',
  components: {
    OnexButton,
    OnexSkeletonLoader,
    ChildRiderExchangePackage,
    AlertsComponent,
  },
  mixins: [openInNewWindow],
  data: () => ({
    offsetTop: 0,
    accepted: false,
    showModal: false,
    mdiFilePdfBox,
    isLoading: false,
  }),
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapState(useChildRiderStore, {
      firstName: 'NPFirstName',
      lastName: 'NPLastName',
      selectedPackage: 'NPPackage',
      selectedOptionDetails: 'NPSelectedOptionDetails',
      acknowledgementHTML: 'NPAcknowledgementHTML',
      loading: 'loading',
    }),
    generationDate() {
      return `(${formatDate(this.selectedPackage?.generationDate, this.globalStore.dateFormat)})`;
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  methods: {
    ...mapActions(useChildRiderStore, {
      viewPDF: 'openPDFPackage',
      crcSubmit: 'crcSubmit',
      setLoading: 'setLoading',
      pullAgreement: 'pullAgreement',
    }),

    onScroll(e: Event) {
      const target = e.target as HTMLElement;
      this.offsetTop = target.scrollTop;
    },
    async openPDF() {
      try {
        const respUrl = await this.viewPDF(DOCUMENT_OBJECT_TYPES.CRC);
        const isWeb = this.$pc2capacitor.isWeb();
        await this.openInNewWindow({ url: respUrl, target: '_blank', inappbrowser: !isWeb });
      } catch (error) {
        this.globalStore.addError({ error, context: 'childrider' }, { root: true });
      }
    },
    async submit() {
      let i;
      try {
        this.setLoading(true);
        this.isLoading = true;
        await this.crcSubmit();
        /*
         * Emit a custom action when a user successfully submits a child rider conversion request
         * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
         */
        i = this.$dynatrace.enterAction('CRC_successful_submission');
        this.$dynatrace.leaveAction(i);
      } catch (error) {
        this.globalStore.addError({ error, context: 'childrider' }, { root: true });
      } finally {
        this.isLoading = false;
        this.setLoading(false);
      }
    },
  },
  watch: {
    locale(newData, oldData) {
      if (newData !== oldData) {
        this.pullAgreement('C');
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.i_agree_btn {
  width: 250px;
}
.color-primary-text {
  color: #02579e;
}
</style>
